/**
 * The practice-DOCUMENTS panel in the patient file — `tasks/practice-documents.md` §7 phase 2.
 *
 * Sits directly under FORMS in `patient-file.component.html`. A document request and a form
 * request are the same kind of thing to a practice user — "we asked the patient for something and
 * we are waiting" — so they are neighbours, and §0 decision 1 is that they SIT BESIDE each other
 * rather than one replacing the other.
 *
 * ── THE REVIEW STATE IS WHAT MAKES THIS PANEL DIFFERENT FROM THE FORMS ONE ───────────────────
 *
 * §0 decision 3: verifying that a returned document is the right one and actually filled in is
 * **a person's job**. There is no fingerprint, no fill detection, no "is this the right form"
 * check — so every return lands on `awaiting_review` and stays there until somebody in the
 * practice accepts it or rejects it with a note. A returned document with no review state on
 * screen would be a document nobody knows to look at, which is the whole failure this panel
 * exists to prevent.
 *
 * A REJECTED request is resendable, and that is the point of rejecting one: the note says what was
 * wrong, and the resend asks again.
 *
 * ── A REJECTION RE-OPENS THE SAME ROW — IT DOES NOT MAKE A SECOND ONE ────────────────────────
 *
 * The server puts the request back to `sent`, clears `returnedAt` and deactivates the files that
 * came back, keeping `reviewStatus: 'rejected'`. So a row that is BOTH `sent` and `rejected` is not
 * a contradiction, it is "we looked at what they sent, it was wrong, and we have asked again" — and
 * it is read here as exactly that, with its own chip wording. Reading the two fields independently
 * would draw a row that says `Awaiting patient` and `Rejected` side by side with no relation
 * between them, which is the kind of screen a receptionist stops trusting.
 *
 * The superseded files are still fetched and still shown, marked as replaced. They are the evidence
 * for the note, and a reviewer resending a document needs to be able to see what was wrong with the
 * last attempt — but they must never be mixed in with a live return, so they are a separate list.
 *
 * ── THERE IS NO COPY-LINK BUTTON, AND THAT IS DELIBERATE ─────────────────────────────────────
 *
 * The raw link is unrecoverable by anything, including the server, and nothing here renders a
 * token or a digest.
 *
 * ── A RESEND IS AN **INVITE** RESEND — §21.1 ─────────────────────────────────────────────────
 *
 * Documents ride the `PatientInvite` rail (§19). There is no per-document link any more, so there
 * is nothing per-document to rotate: the resend re-sends THE SAME invite link, and whatever is
 * outstanding for the patient is picked up fresh by `loadInviteById` when they open it. A document
 * added after the first send needs no new link at all.
 *
 * The button is therefore addressed to `request.patientInviteId`, and the cooldown that governs it
 * is the INVITE'S. Two documents delivered on one invite share that cooldown, so resending either
 * disables both — which is the truth: one message carries both.
 *
 * The standalone rail's `resendRequest` rotated the token, so the link already in the patient's
 * inbox stopped working. That endpoint is gone, and so is the confirmation copy that warned about
 * it: a resend that told a patient their old link had died would now be describing something that
 * does not happen.
 *
 * ── A ROW WITH NO INVITE CANNOT BE RESENT, AND SAYS SO ───────────────────────────────────────
 *
 * `patientInviteId` is NULL on any row raised before documents moved onto the invite rail. There
 * is no link this client can send those patients and no endpoint left that could mint one, so the
 * button is not offered — and the tooltip on the row says the practice must send the document
 * again rather than leaving a receptionist clicking a control that cannot work.
 *
 * ── SIX STATUSES, NOT FIVE ───────────────────────────────────────────────────────────────────
 *
 * §3.3 has `scheduled` as well as `sent`. Phase 2 only ever creates `sent` rows — a manual send
 * goes out immediately — but the appointment-type scheduling of Phase 4 creates asks that exist
 * for days before anyone is given them, and an ask nobody has seen must never read like one the
 * patient has ignored. The label is here from the start rather than retrofitted.
 */

import {
  Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild,
} from '@angular/core';
// `MatDialogRef` is back: §8's "file it yourself" dialog is an `ng-template` this component opens
// and holds, the arrangement the §26 validate dialog used before it moved to a shared component.
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
// §26 review, BOTH STAGES, shared with the Inbox - `tasks/inbox.md` §11.
import {
  DocumentReviewDialogComponent,
  DocumentReviewDialogData,
  DocumentReviewDialogResult,
} from '../../../../shared/components/document-review-dialog/document-review-dialog.component';
import { MatExpansionPanel } from '@angular/material/expansion';
import { interval, takeUntil } from 'rxjs';

import { BaseComponent } from '../../../../core/components/base/base.component';
/**
 * THE GALLERY'S OWN MAPPER, not a private one.
 *
 * `patient-clinical-timeline.component.html` line 147 feeds `<app-file-gallery>` through
 * `mapMediaToFileObject`, and so does every other gallery in the product. A second mapping here
 * would be a second opinion about what a page is — including about the one rule that matters on
 * this screen, that a PDF is filtered OUT of a gallery — and the two would be found disagreeing by
 * somebody comparing a returned document with the same pages on the timeline.
 */
import { FileObject, mapMediaToFileObject } from '../../../../core/interfaces/file-object';
import { FeedbackService } from '../../../../core/services/feedback.service';
import {
  PatientDocumentRequestDto,
  PatientDocumentRequestService,
  PatientDocumentRequestStatus,
  PatientDocumentReturnedFileDto,
  activeReturnedFiles,
  activeValidationFiles,
  describeDocumentFailure,
  supersededReturnedFiles,
} from '../../../../core/services/patient-document-request.service';
import {
  PatientInviteService,
  formatCooldownRemaining,
  getResendCooldown,
} from '../../../../core/services/patient-invite.service';
/**
 * §25.4 — THE THREE SKIP REASONS AND THEIR THREE DIFFERENT REMEDIES.
 *
 * Imported rather than reimplemented, for the reason `mapMediaToFileObject` is imported above: this
 * panel is one of three screens that answer "why was this never sent", and three screens inventing
 * their own wording is three chances for one of them to send a receptionist after the wrong fix.
 */
import {
  DocumentSkipDescription,
  DocumentSkipRow,
  PatientDocumentSkipService,
  describeDocumentSkip,
  isDocumentSkipped,
} from '../../../../core/services/patient-document-skip.service';
import { PatientService } from '../../../../core/services/patient.service';
import { SocketService } from '../../../../core/services/socket.service';
import { PatientDocumentSendDialogComponent } from './patient-document-send-dialog.component';
import { inject } from '@angular/core';
import { GroupService } from 'src/app/core/services/group.service';
import { isStateModeGroup } from 'src/app/shared/util/state-mode.util';
/*
 * `documentScoreLabel` WAS IMPORTED HERE, for the score chip inside the AI SUMMARY block. That
 * block is gone (9 Sep 2026, owner's call) and nothing on this panel draws a score any more.
 *
 * The function is NOT deleted and must not be: `clinical-timeline-documents.ts` still uses it for
 * the timeline's document row, its "NULL is not zero" specs and mutation anchor live beside it,
 * and the two score columns are still on the DTO for that reason.
 */

/**
 * Wording aimed at a practice user. `sent` is the state a receptionist calls "waiting"; `scheduled`
 * is the one they have not sent yet, and the two must not read alike.
 */
const STATUS_LABELS: { [key: string]: string } = {
  scheduled: 'Not sent yet',
  sent: 'Awaiting patient',
  /*
   * `in_progress` STAYS IN THE VOCABULARY, BUT NOTHING WRITES IT FOR A DOCUMENT.
   *
   * It is inherited from `PatientFormRequest`, where it is real: a form holds an AUTOSAVE DRAFT,
   * so `publicPatientFormController` stamps `in_progress` and a `startedAt` the first time a
   * patient saves a partial answer set. A document has no draft — the patient either returns
   * files or does not — so there is no moment to stamp and no state to resume.
   *
   * A `startedAt` field rode across with it, was declared on the DTO, and was rendered here as
   * "Opened …" against a column that does not exist. It never drew anything, and it cost a wave
   * a harness run when it was copied into an export whitelist and MSSQL answered
   * `Invalid column name 'startedAt'`. Removed 9 Sep 2026, owner's call.
   *
   * The STATUS is kept: it is in `CK_PatientDocumentRequests_status` and in `openStatuses`, so
   * dropping it means a migration for no benefit, and it is the natural home if Tier A ever
   * records "the patient opened the viewer". If that day comes, add the column FIRST — do not
   * reintroduce a field the server does not send.
   */
  in_progress: 'Opened',
  completed: 'Returned',
  expired: 'Link expired',
  revoked: 'Cancelled',
  // §14.4 - an information-only document that went out. NOT an outstanding ask: nobody is
  // waiting on the patient, there is no link to expire and there is nothing to review. It reads
  // as the finished fact it is, and never as a row in the practice's waiting queue.
  delivered: 'Sent — nothing to return',
  /*
   * §23.1 / §25 — THE PATIENT WAS NEVER ASKED, and the row exists to say so out loud.
   *
   * Terminal, and distinct from `revoked`: revoked means the practice withdrew an ask the patient
   * had already been given, skipped means they were never given one. Collapsing the two makes
   * "did the patient ever see this?" unanswerable. The chip is short because the WHY and the
   * REMEDY are a strip of their own below the row — three reasons, three different things to do.
   */
  skipped: 'Not sent',
};

/** The review state (§0 decision 3), which only exists once something has come back. */
const REVIEW_LABELS: { [key: string]: string } = {
  awaiting_review: 'Needs review',
  // §26 — `accepted` is a WAYPOINT now, and the label has to say so or a practice reads a
  // signed-off document as a finished one and stops looking for it.
  accepted: 'Signed off — needs validation',
  rejected: 'Rejected',
  validated: 'Validated',
};

/**
 * The one review label that depends on the STATUS as well as the review.
 *
 * A rejection re-opens the ask, so `rejected` on a row that is no longer `completed` means the
 * patient has been asked again — not that the practice is still holding a rejected document.
 */
const REJECTED_AND_REASKED = 'Rejected \u2014 asked again';

@Component({
  selector: 'app-patient-document-list',
  templateUrl: './patient-document-list.component.html',
  styleUrls: ['./patient-document-list.component.scss'],
})
export class PatientDocumentListComponent extends BaseComponent implements OnInit {
  private readonly groupService = inject(GroupService);

  /** A state unit (tasks/state-mode.md): no document is sent to the patient. */
  get stateMode(): boolean {
    return isStateModeGroup(this.groupService.group);
  }


  /** The panel contract every sibling in the accordion uses. Do not change its shape. */
  @Input() initialExpanded?: boolean;
  expanded?: boolean;
  @Output() expandedChange = new EventEmitter<boolean>();

  /**
   * So `focusRequest` can OPEN the panel. `[expanded]` is bound to `initialExpanded` exactly as
   * every sibling panel binds it — a one-way input the parent owns — so the only honest way to
   * open ourselves is to ask the panel, which then emits `(opened)` and keeps the parent in step.
   */
  @ViewChild(MatExpansionPanel) panel?: MatExpansionPanel;

  /** `null` while the first load is in flight; `[]` once we know there are none. */
  requests: PatientDocumentRequestDto[] | null = null;

  /** Which row is open. One at a time — two sets of returned scans side by side help nobody. */
  expandedRequestId: string | null = null;

  /** `GET /patient-document-requests/:id` results, keyed by request id. */
  details: { [requestId: string]: PatientDocumentRequestDto } = {};
  detailLoading: { [requestId: string]: boolean } = {};
  detailError: { [requestId: string]: string } = {};

  /** Ticks every second so the resend cooldown countdown moves. Same idiom as the consent panel. */
  cooldownTick = 0;

  busyRequestId: string | null = null;

  constructor(
    private patientDocumentRequestService: PatientDocumentRequestService,
    // §21.1 — a resend is an INVITE resend, so the panel talks to the invite service for it. There
    // is no per-document resend endpoint any more.
    private patientInviteService: PatientInviteService,
    // §23.1 — send-now on a skipped row. A DIFFERENT action from a resend: there is no invite to
    // resend, because nothing was ever sent.
    private patientDocumentSkipService: PatientDocumentSkipService,
    private patientService: PatientService,
    /*
     * `socketService` IS STILL INJECTED AND IS STILL UNUSED BY THIS COMPONENT'S OWN CODE — and
     * that is worth one line rather than a silent removal. It was added for
     * `patientDocumentAiRead`, which is gone on every side (see `ngOnInit`). It is kept because
     * `BaseComponent`'s subclasses in this folder are constructed by the TestBed with a
     * `SocketService` provider and because the panel is the natural home for the next per-row
     * event; if that has not happened by the time somebody next reads this, take it out.
     */
    private socketService: SocketService,
    fs: FeedbackService,
    md: MatDialog,
  ) {
    super(fs, md);
  }

  ngOnInit(): void {
    this.patientDocumentRequestService.patientDocumentRequests$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (requests) => {
          this.requests = requests;
          // A row that has gone away (patient switched) must not keep a stale detail open.
          if (this.expandedRequestId && requests
            && !requests.some((r) => r.id === this.expandedRequestId)) {
            this.expandedRequestId = null;
          }
        },
        error: (error: any) => {
          console.error('Error loading patient document requests: ', error);
          this.requests = [];
        },
      });

    // The send dialog asks for this when a dedupe 409 named the request that blocked the send.
    this.patientDocumentRequestService.focusRequest$
      .pipe(takeUntil(this.destroy$))
      .subscribe((id) => this.focusRequest(id));

    /* ── `patientDocumentAiRead` WAS LISTENED FOR HERE ─────────────────────────────────────
     *
     * §27.6's loose end: the panel caches the detail copy per row and `loadDetail` returns early
     * while that cache exists, so a row that was ALREADY EXPANDED when the read landed showed the
     * chip but not the text. This listener dropped `details[id]` and re-read.
     *
     * The whole mechanism is gone — 9 Sep 2026, owner's call. There is no summary on this screen
     * to refresh: the "AI SUMMARY" block was removed from the template, the service's row patch
     * went with it, and `services/patientDocumentAiService.js` no longer emits the event at all.
     * The READ still runs and still writes its columns; it feeds Cura, which loads its own context.
     *
     * `lessons.md` §5b is why all three halves went together rather than one: a listener that
     * patches a field nothing draws is "computed, then discarded" with a socket in front of it. */

    interval(1000).pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.cooldownTick = Date.now();
    });
  }

  toggleExpanded(): void {
    this.expanded = !this.expanded;
    this.expandedChange.emit(this.expanded);
  }

  /* ── Row presentation ─────────────────────────────────────────────────────────────────── */

  documentName(request: PatientDocumentRequestDto): string {
    return (request.PracticeDocument && request.PracticeDocument.name) || 'Document';
  }

  statusLabel(request: PatientDocumentRequestDto): string {
    const label = STATUS_LABELS[request.status];
    return label ? label : request.status;
  }

  /** Drives the chip colour. One class per status — the six are visually distinct. */
  statusClass(request: PatientDocumentRequestDto): string {
    return `status-${request.status}`;
  }

  isStatus(request: PatientDocumentRequestDto, status: PatientDocumentRequestStatus): boolean {
    return request.status === status;
  }

  /** `''` when nothing has ever come back — a review state on an unanswered ask means nothing. */
  reviewLabel(request: PatientDocumentRequestDto): string {
    const status = request.reviewStatus;
    if (!status) return '';
    // See `REJECTED_AND_REASKED`: `rejected` + not `completed` is a re-opened ask, not a
    // contradiction, and the two must not read as unrelated facts sitting next to each other.
    if (status === 'rejected' && request.status !== 'completed') return REJECTED_AND_REASKED;
    return REVIEW_LABELS[status] || status;
  }

  reviewClass(request: PatientDocumentRequestDto): string {
    return `review-${request.reviewStatus}`;
  }

  hasReview(request: PatientDocumentRequestDto): boolean {
    return this.reviewLabel(request) !== '';
  }

  /**
   * When the patient was last asked — a resend moves this, which is what the practice wants.
   *
   * NOTHING for a skipped row. `createdAt` is the fallback for an unsent `scheduled` row, where it
   * is a fair reading of "asked on"; on a `skipped` row it would print "Sent 9 September" against
   * an ask the patient was never given, which is the one thing this whole state exists to prevent.
   */
  sentAt(request: PatientDocumentRequestDto): string | null | undefined {
    if (this.isSkipped(request)) return null;
    return request.lastSentAt || request.sentAt || request.createdAt;
  }

  /* ── SKIPPED ROWS — §25.4's three reasons and their three remedies ────────────────────────
   *
   * The panel is where a practice meets a document that never went out, so the row has to carry
   * WHY and WHAT TO DO, not just "Not sent". The vocabulary is
   * `core/services/patient-document-skip.service`; this component decides only where it is drawn.
   * ─────────────────────────────────────────────────────────────────────────────────────── */

  /**
   * Descriptions are CACHED per row-state.
   *
   * `describeDocumentSkip` builds a new object each call, and a template binding that returns a
   * fresh object every change-detection pass makes `*ngIf` re-create its subtree on every tick.
   * The key carries the two fields the description depends on, so a row that changes state (a
   * send-now landing) recomputes and a row that has not does not.
   */
  private skipCache: { [key: string]: DocumentSkipDescription | null } = {};

  /** The whole description for a skipped row, or `null` on any row that was actually sent. */
  skipDescription(request: PatientDocumentRequestDto): DocumentSkipDescription | null {
    // Assigned, not cast: `PatientDocumentRequestDto` structurally satisfies `DocumentSkipRow`,
    // which is what lets this panel read `skipReason` while that field is still being added to the
    // DTO in a file another wave owns.
    const row: DocumentSkipRow = request;
    const key = `${row.id}|${row.status}|${row.skipReason || ''}`;
    if (!(key in this.skipCache)) this.skipCache[key] = describeDocumentSkip(row);
    return this.skipCache[key];
  }

  isSkipped(request: PatientDocumentRequestDto): boolean {
    return isDocumentSkipped(request);
  }

  /**
   * Is "Send now" worth offering on this row?
   *
   * FALSE on an `unreachable` row, and that is a decision rather than an oversight — see
   * `DocumentSkipDescription.canSendNow`. A button that marked the row `sent` and delivered it
   * nowhere would put the row into exactly the state §25.3 stopped the job producing.
   */
  canSendNow(request: PatientDocumentRequestDto): boolean {
    if (this.stateMode) return false;
    const skip = this.skipDescription(request);
    return !!skip && skip.canSendNow;
  }

  /** The reason a skipped row offers no Send-now button. Empty when it does, or is not skipped. */
  sendNowUnavailableReason(request: PatientDocumentRequestDto): string {
    const skip = this.skipDescription(request);
    if (!skip || skip.canSendNow) return '';
    return skip.remedy;
  }

  /**
   * §23.1 — send it now, and DELIVER inside the request.
   *
   * The server re-schedules the row to now, clears `skippedAt` and sends; the panel re-reads
   * rather than patching the row by hand, because the response's row is the one the server just
   * wrote and guessing at it here is how two screens start disagreeing.
   */
  async sendNow(request: PatientDocumentRequestDto, event?: MouseEvent): Promise<void> {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.canSendNow(request) || this.busyRequestId) return;

    const confirmed = await this.showConfirmationDialog(
      'SEND THIS DOCUMENT NOW',
      `"${this.documentName(request)}" was never sent to this patient. Send it now? It goes out `
      + `straight away, by email and text message.`,
      undefined,
      'Cancel',
      'Send now',
    );
    if (!confirmed) return;

    this.busyRequestId = request.id;

    this.patientDocumentSkipService.sendNow(request.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.busyRequestId = null;
          this.showToastMessage(`"${this.documentName(request)}" was sent to the patient.`);
          this.patientDocumentRequestService.refresh();
        },
        error: (error: any) => {
          this.busyRequestId = null;
          // The server's own words wherever it sent any: `NOT_SENDABLE`, `DOCUMENT_INACTIVE` and
          // `DOCUMENT_FILE_MISSING` each already say what the practice has to fix, and
          // paraphrasing them here would only lose the instruction.
          const failure = describeDocumentFailure(error, this.documentName(request));
          this.showToastMessage(failure.message);
          // A 409 means our copy is stale — somebody sent it from another screen. Re-read rather
          // than leaving a button on offer that the server will refuse again.
          this.patientDocumentRequestService.refresh();
        },
      });
  }

  /**
   * The file the patient was actually given (§11).
   *
   * NULL is ordinary and means "the document's current file" — every row created before the pin
   * existed, and every `scheduled` row not yet sent. A reviewer holding a returned scan against a
   * blank that has since been replaced is exactly who this line is for, so it is shown when it is
   * known and simply omitted when it is not, never guessed at.
   */
  sentFileName(request: PatientDocumentRequestDto): string {
    return request.sentMediaFileName || '';
  }

  /* ── Expanding a row ──────────────────────────────────────────────────────────────────── */

  /**
   * A row is openable once anything has ever come back against it.
   *
   * `completed` is the obvious case. A REJECTED-AND-RE-ASKED row is the other one, and it matters:
   * its files are deactivated and its status is back to `sent`, but the note saying what was wrong
   * and the pages it was written about are the only things whoever resends has to go on. Hiding
   * them behind `status === 'completed'` would put the evidence out of reach at exactly the moment
   * somebody needs it.
   */
  canOpen(request: PatientDocumentRequestDto): boolean {
    return request.status === 'completed' || !!request.reviewStatus;
  }

  /** What the expand hint says — a re-opened row is not offering "what came back" any more. */
  openHint(request: PatientDocumentRequestDto): string {
    const reasked = request.reviewStatus === 'rejected' && request.status !== 'completed';
    if (reasked) return this.isOpen(request) ? 'Hide the rejected pages' : 'View the rejected pages';
    return this.isOpen(request) ? 'Hide what came back' : 'View what came back';
  }

  isOpen(request: PatientDocumentRequestDto): boolean {
    return this.expandedRequestId === request.id;
  }

  toggleRow(request: PatientDocumentRequestDto): void {
    if (!this.canOpen(request)) return;
    if (this.expandedRequestId === request.id) {
      this.expandedRequestId = null;
      return;
    }
    this.expandedRequestId = request.id;
    /*
     * `cancelReject()` WAS CALLED HERE, TWICE, and is gone with the inline reject box
     * (`tasks/inbox.md` §11). It existed to close a note box drawn INSIDE the expanded row, so
     * that collapsing the row or opening a different one could not leave a half-typed rejection
     * attached to the wrong document.
     *
     * A dialog cannot have that problem: it is modal, it owns its own state, and it is destroyed
     * when it closes. There is nothing left for a row collapse to tidy up.
     */
    this.loadDetail(request.id, request.groupId);
  }

  /** `groupId` is the request's own practice — §32. Every caller has the row in hand. */
  private loadDetail(id: string, groupId: string): void {
    if (this.details[id] || this.detailLoading[id]) return;
    this.detailLoading[id] = true;
    delete this.detailError[id];

    this.patientDocumentRequestService.loadRequestById(id, groupId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result: any) => {
          this.detailLoading[id] = false;
          if (result && result.data) {
            this.details[id] = result.data;
          } else {
            this.detailError[id] = 'What the patient sent back could not be loaded.';
          }
        },
        error: (error: any) => {
          this.detailLoading[id] = false;
          console.error('Error loading document request detail: ', error);
          this.detailError[id] =
            'What the patient sent back could not be loaded. Please try again.';
        },
      });
  }

  /**
   * The files that still stand. Several of them, routinely — a photograph per page (§3.4).
   *
   * Read off the DETAIL only. The list endpoint sends `returnedFileCount` and no array, so reading
   * the row here would draw an empty list on a request that returned four photographs — which looks
   * exactly like a patient who sent nothing.
   */
  filesFor(request: PatientDocumentRequestDto): PatientDocumentReturnedFileDto[] {
    return activeReturnedFiles(this.details[request.id]);
  }

  /** What a rejection replaced. Shown apart and labelled, never mixed in with the live files. */
  supersededFilesFor(request: PatientDocumentRequestDto): PatientDocumentReturnedFileDto[] {
    return supersededReturnedFiles(this.details[request.id]);
  }

  /**
   * How many files the practice currently holds, for the row.
   *
   * `returnedFileCount` counts ACTIVE files only, so after a rejection it is 0 while
   * `returnedFiles` still carries the superseded generation. That disagreement is the server being
   * precise, not inconsistent, and the row shows the count because the row is about what is held.
   */
  fileCountLabel(request: PatientDocumentRequestDto): string {
    const count = request.returnedFileCount;
    if (count === null || count === undefined || count < 1) return '';
    return count === 1 ? '1 file' : `${count} files`;
  }

  fileName(media: PatientDocumentReturnedFileDto, index: number): string {
    return media.mediaFileName || `Page ${index + 1}`;
  }

  /** An icon that says what kind of thing this is without printing a MIME type at a receptionist. */
  fileIcon(media: PatientDocumentReturnedFileDto): string {
    const type = (media.mediaType || '').toLowerCase();
    if (type.indexOf('image/') === 0) return 'image';
    return 'picture_as_pdf';
  }

  /**
   * A file whose blob reference could not be parsed comes back with `url: null`, deliberately —
   * the server returns the row so a reviewer learns the page exists and cannot be opened, rather
   * than silently seeing one fewer page than the patient sent.
   */
  canOpenFile(media: PatientDocumentReturnedFileDto): boolean {
    return !!media.url;
  }

  /** The reviewer's own note, once a decision has been made. */
  reviewNoteFor(request: PatientDocumentRequestDto): string {
    const detail = this.details[request.id];
    return (detail && detail.reviewNote) || request.reviewNote || '';
  }

  reviewedAtFor(request: PatientDocumentRequestDto): string | null | undefined {
    const detail = this.details[request.id];
    return (detail && detail.reviewedAt) || request.reviewedAt;
  }

  /* ── THE GALLERY — the clinical timeline's own idiom ─────────────────────────────────────
   *
   * `patient-clinical-timeline.component.html` line 147:
   *
   *     <app-file-gallery [fileObjects]="item.media" [deleteEnabled]="false" ...>
   *
   * A returned document is a stack of pages and §0 decision 3 says a PERSON looks at them. The
   * panel used to draw one link per page; a reviewer checking a ten-page SAQ opened ten tabs.
   * ──────────────────────────────────────────────────────────────────────────────────────── */

  /**
   * THE GALLERY IS CACHED PER ROW-STATE, for the reason `skipCache` above is.
   *
   * `mapMediaToFileObject` builds a NEW array of NEW objects on every call, and
   * `FileGalleryComponent.ngOnChanges` fires on every change-detection pass that hands it one —
   * which, for a binding that returns a fresh array, is every tick. The key carries the ids and
   * the urls, so a re-read that mints fresh SAS links rebuilds it and a mouse move does not.
   */
  private galleryCache: { [key: string]: FileObject[] } = {};

  /**
   * The returned pages, in the shape `<app-file-gallery>` takes.
   *
   * ── WHY THIS RESHAPES BEFORE IT MAPS ─────────────────────────────────────────────────────
   *
   * `mapMediaToFileObject` reads MEDIA ROWS — `mediaUrl`, `mediaType`, `displayIndex` — and the
   * detail endpoint serves a MAPPED DTO whose link is called `url` and is short-lived (`mediaUrl`
   * never leaves the server: §10.1 decision 3 stores it unsigned and a SAS is minted per read). So
   * the DTO is put back into the media shape the mapper is written against, rather than the mapper
   * being taught a second shape. That is the §21 rule applied forwards: the mapper keeps seeing
   * exactly what every other gallery in the product feeds it.
   *
   * ── WHAT IT DROPS, AND WHY THAT IS NOT A LOSS ────────────────────────────────────────────
   *
   * Rows with no `url` are excluded here, not by the mapper: `mapMediaToFileObject` returns its
   * input UNCHANGED when the first row has no `mediaUrl`, and the gallery would then read `.type`
   * off a raw DTO and throw. They are not lost — `fallbackFilesFor` renders them as the
   * "cannot be opened" line the server returns them for.
   *
   * PDFs are dropped by the MAPPER, which filters them out of every gallery. Since the return path
   * converts a returned PDF into page images, a PDF row here means that conversion failed; it is
   * rendered by `fallbackFilesFor` as a link the reviewer can open.
   */
  galleryFor(request: PatientDocumentRequestDto): FileObject[] {
    const files = this.filesFor(request);
    const key = `${request.id}|${files.map((f) => `${f.id}:${f.url || ''}`).join(',')}`;
    if (!(key in this.galleryCache)) {
      this.galleryCache[key] = mapMediaToFileObject(
        files
          .filter((file) => !!file.url)
          .map((file) => ({
            id: file.id,
            mediaUrl: file.url,
            mediaType: file.mediaType,
            mediaFileName: file.mediaFileName,
            displayIndex: file.displayIndex,
            patientId: request.patientId,
          })),
      ) || [];
    }
    return this.galleryCache[key];
  }

  /**
   * The active files the GALLERY CANNOT DRAW, so that nothing the patient sent goes missing.
   *
   * Two kinds, and both have to reach a reviewer:
   *
   *   · a PDF — `mapMediaToFileObject` filters PDFs out of a gallery. Since the return path
   *     converts (`utils/patientDocumentRequestUtils.convertReturnedPdfToPages`), a PDF row means
   *     the conversion FAILED. The return is filed and whole; the reviewer opens the document
   *     instead of scrolling its pages, and this is the line that lets them.
   *   · a row with no `url` — the server returns it deliberately so a reviewer learns a page
   *     exists and cannot be opened, rather than silently seeing one fewer page.
   *
   * The two lists partition the active files exactly: every file is in the gallery or in this.
   */
  fallbackFilesFor(request: PatientDocumentRequestDto): PatientDocumentReturnedFileDto[] {
    return this.filesFor(request).filter((file) => {
      if (!file.url) return true;
      return String(file.mediaType || '').toLowerCase() === 'application/pdf';
    });
  }

  /* ── The row's record-type icon — the timeline's idiom ────────────────────────────────── */

  /**
   * The icon's colour class.
   *
   * The clinical timeline colours its `.record-type-icon` by what KIND of record it is. Every row
   * on this panel is the same kind, so the useful distinction here is the one the panel exists for
   * (§0 decision 3): a return NOBODY HAS LOOKED AT is the failure this screen prevents, so it is
   * the one that is coloured. `''` — the timeline's own neutral grey — for everything else.
   */
  reviewIconClass(request: PatientDocumentRequestDto): string {
    if (!request.reviewStatus) return '';
    if (request.reviewStatus === 'awaiting_review') return 'awaiting-icon';
    if (request.reviewStatus === 'rejected') return 'rejected-icon';
    // §26. THE BRANCH THAT WOULD HAVE BEEN MISSED: this used to fall through to `accepted-icon`
    // for anything that was not awaiting or rejected, so a validated row — the only state that
    // reaches the clinical timeline — would have drawn as merely signed off.
    if (request.reviewStatus === 'validated') return 'validated-icon';
    return 'accepted-icon';
  }

  /** What the icon says on hover. The timeline names its source; this one names the state. */
  documentIconTooltip(request: PatientDocumentRequestDto): string {
    const label = this.reviewLabel(request);
    return label ? `Practice document \u2014 ${label}` : 'Practice document';
  }

  /* ── Review — §0 decision 3 ───────────────────────────────────────────────────────────── */

  /**
   * A decision is offered on a returned document until one has been taken.
   *
   * `status === 'completed'` is load-bearing: a rejected-and-re-asked row is back on `sent`, and
   * offering Accept there would let somebody accept a document the patient has not sent yet.
   */
  canReview(request: PatientDocumentRequestDto): boolean {
    return request.status === 'completed'
      && (!request.reviewStatus || request.reviewStatus === 'awaiting_review')
      // §26. `!== false`, NEVER `=== true`. A server that has not shipped §26 sends neither flag,
      // and reading absent as "forbidden" would disable every review button in the product. The
      // same reasoning as the `startedAt` note at the top of this file.
      && request.canSignOff !== false;
  }

  /**
   * §26 STAGE TWO — offered only on a document somebody has already signed off.
   *
   * The ORDER is the workflow: a clinician does not see a Validate button on a document nobody
   * has checked is the right one. The server refuses it too (`409 NOT_SIGNED_OFF`); this only
   * keeps a button off the screen that would be refused if pressed.
   */
  canValidate(request: PatientDocumentRequestDto): boolean {
    return request.status === 'completed'
      && request.reviewStatus === 'accepted'
      && request.canValidate !== false;
  }

  /** True once a clinician has put this on the timeline. Nothing further is offered. */
  isValidated(request: PatientDocumentRequestDto): boolean {
    return request.reviewStatus === 'validated';
  }

  /* ════════════════════════════════════════════════════════════════════
   * "I ALREADY HAVE THIS DOCUMENT" — the practice closes the loop itself
   *
   * `tasks/document-rejection-resend.md` §7. The patient emailed the form back, walked in with a
   * printed copy, posted it, or is simply never going to use the link. Until this existed the only
   * options were to leave the ask open for ever or to REVOKE it — which records that the practice
   * withdrew the ask, not that it was answered.
   * ═══════════════════════════════════════════════════════════════════ */

  /**
   * Offered on an OPEN ASK and nothing else.
   *
   * The three exclusions each mean something different, and the server refuses all three too — this
   * only keeps a button off the screen that would be refused if pressed:
   *
   *   · already RETURNED — there are pages on the row. Replacing them is a REJECTION followed by a
   *     new return, so that what was superseded is recorded rather than overwritten.
   *   · `delivered` — §14.2: an information sheet is never returned, so there is nothing to file.
   *   · `revoked` / `expired` — the ask is not live. `skipped` IS offered: the patient was never
   *     asked, and them turning up with the form anyway is exactly the case this exists for.
   */
  canFileReturn(request: PatientDocumentRequestDto): boolean {
    if (request.status === 'completed' || request.returnedAt) return false;
    if (request.status === 'delivered') return false;
    if (request.status === 'revoked' || request.status === 'expired') return false;
    return true;
  }

  /** The picker's own array while the dialog is open, and the row it belongs to. */
  filing: {
    request: PatientDocumentRequestDto | null;
    media: FileObject[];
    sending: boolean;
    error: string | null;
  } = { request: null, media: [], sending: false, error: null };

  @ViewChild('fileReturnDialog') fileReturnDialog?: TemplateRef<any>;
  private fileReturnDialogRef: MatDialogRef<any> | null = null;

  /**
   * Seeded BEFORE the dialog opens and reset wholesale, so pages picked for one document can never
   * be submitted against another. The idiom the validate dialog used, and
   * `patient-ai-request-detail` before it.
   */
  openFileReturn(request: PatientDocumentRequestDto, event?: Event): void {
    if (event) event.stopPropagation();
    if (!this.canFileReturn(request) || !this.fileReturnDialog) return;

    this.filing = { request, media: [], sending: false, error: null };
    this.fileReturnDialogRef = this.dialog.open(this.fileReturnDialog, { width: '560px' });
  }

  closeFileReturn(): void {
    if (this.fileReturnDialogRef) this.fileReturnDialogRef.close();
    this.fileReturnDialogRef = null;
    // The object URLs are revoked HERE and nowhere earlier: the gallery draws from them for as long
    // as the dialog is open, so releasing one on pick would blank the tile it just added.
    for (const item of this.filing.media) {
      if (item && item.url) URL.revokeObjectURL(item.url);
    }
    this.filing = { request: null, media: [], sending: false, error: null };
  }

  /**
   * `app-file-upload` accumulates into its OWN array and emits the WHOLE of it on every pick, so
   * this REPLACES rather than appends. Appending would double every page the moment a second was
   * added. Deduped by `url`, so a re-emit of an unchanged array does not grow it.
   */
  onFilingPicked(picked: FileObject[]): void {
    const seen = new Set<string>();
    this.filing.media = (picked || []).filter((item) => {
      if (!item || !item.url || seen.has(item.url)) return false;
      seen.add(item.url);
      return true;
    });
  }

  onFilingDeleted(removed: FileObject): void {
    if (!removed) return;
    this.filing.media = this.filing.media.filter((item) => item.url !== removed.url);
    if (removed.url) URL.revokeObjectURL(removed.url);
  }

  onFilingEdited(edited: FileObject[]): void {
    this.filing.media = edited || [];
  }

  /**
   * The `File`s the request posts, rebuilt from the picker's `blob:` URLs at SUBMIT time.
   *
   * `app-file-upload` hands over `FileObject`s whose original `File` is gone, so each is fetched
   * back into a blob. Doing it here rather than on pick is what lets the gallery keep drawing from
   * those URLs in the meantime — the same rebuild `practice-document-edit-dialog` does.
   */
  private async filesFromFiling(): Promise<File[]> {
    const files: File[] = [];
    for (const item of this.filing.media) {
      if (!item || !item.url) continue;
      const blob = await (await fetch(item.url)).blob();
      files.push(new File([blob], item.name || `page-${Date.now()}`,
        { type: item.type || blob.type || 'application/octet-stream' }));
    }
    return files;
  }

  async submitFileReturn(): Promise<void> {
    const request = this.filing.request;
    if (!request || this.filing.sending) return;

    if (!this.filing.media.length) {
      this.filing.error = 'Attach the completed document — a scan, or a photograph of each page.';
      return;
    }

    let files: File[];
    try {
      files = await this.filesFromFiling();
    } catch {
      this.filing.error = 'Those files could not be read from this device. Try attaching them again.';
      return;
    }

    this.filing.sending = true;
    this.filing.error = null;
    this.busyRequestId = request.id;

    this.patientDocumentRequestService.fileReturn(request.id, files)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result: any) => {
          this.filing.sending = false;
          this.busyRequestId = null;
          /*
           * The DETAIL COPY IS DROPPED AND RE-READ. The server now holds pages this client has
           * never seen and their links are minted per read, so without the re-read the expanded
           * row would draw an empty gallery against a document that is demonstrably returned.
           */
          delete this.details[request.id];
          this.loadDetail(request.id, request.groupId);
          this.closeFileReturn();
          this.showToastMessage((result && result.message)
            || `"${this.documentName(request)}" was filed. It is now waiting to be signed off.`);
        },
        error: (error: any) => {
          this.filing.sending = false;
          this.busyRequestId = null;
          const failure = describeDocumentFailure(error, this.documentName(request));
          this.filing.error = failure.message;
          /*
           * ALREADY_RETURNED means somebody else — or the patient — filed it while this dialog was
           * open. The held copy is stale, so the row is re-read and the dialog closed rather than
           * left offering a write the server will refuse again.
           */
          if (failure.code === 'ALREADY_RETURNED') {
            delete this.details[request.id];
            this.patientDocumentRequestService.refresh();
            this.closeFileReturn();
            this.showToastMessage(failure.message);
          }
        },
      });
  }

  /**
   * BOTH REVIEW STAGES NOW LIVE IN `shared/components/document-review-dialog`.
   *
   * `tasks/inbox.md` §11. This panel used to carry its own copy: `accept`, `startReject`,
   * `reject`, `submitReview`, the `validating` scratch object, the `#validateDialog` template and
   * `submitValidation` / `submitValidationRejection` / `sendValidation` — roughly 300 lines,
   * ending here.
   *
   * They are gone because the Inbox (`patients/inbox`) needs the SAME two decisions from a
   * different screen, and two copies of a rule that decides what goes on a patient's clinical
   * record is exactly what this codebase spends `utils/patientRequestDelivery.js` avoiding. The
   * dialog is the one implementation; this panel and the Inbox are its two callers.
   *
   * ── WHAT THIS CHANGED FOR SOMEBODY USING THE PANEL ──────────────────────────────────
   *
   * Stage one used to be INLINE — two buttons and a reject-note box drawn inside the expanded
   * row. It is a dialog now, like stage two already was. That is a real UX change and it is the
   * price of having one implementation; it also makes the two stages behave identically, which
   * they did not before.
   *
   * ── WHY THE DIALOG IS GIVEN AN ID AND NOT THE ROW ───────────────────────────────────
   *
   * It re-reads through `GET /patient-document-requests/:id` itself. The returned files' URLs are
   * short-lived and minted per read, and this panel's `details` copy may be minutes old — a
   * caller holding a stale row must not be able to act on it.
   */
  openReview(request: PatientDocumentRequestDto, event?: Event): void {
    if (event) event.stopPropagation();
    if (!this.canReview(request) && !this.canValidate(request)) return;

    const data: DocumentReviewDialogData = {
      requestId: request.id,
      // §32 — the dialog's read is scoped to this practice.
      groupId: request.groupId,
      patientId: request.patientId || null,
      patientName: null,
    };

    this.dialog.open(DocumentReviewDialogComponent, {
      width: '760px', maxWidth: '96vw', maxHeight: '92vh',
      autoFocus: false, data,
    }).afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: DocumentReviewDialogResult | undefined) => {
        if (!result || !result.changed) return;

        /*
         * The DETAIL COPY IS DROPPED, ALWAYS, and re-read only for a validation.
         *
         * The distinction is the one the old `sendValidation` made and it is not cosmetic. After a
         * validation the server holds attachments and observations this client has never seen, and
         * their links are minted per read — so the detail has to be fetched again or the gallery
         * draws nothing. After a rejection the row is back on `sent` with its pages deactivated:
         * there is nothing left to show, `canOpen` is false, and a re-read would be a request for
         * a detail nobody is going to look at.
         */
        delete this.details[request.id];
        if (result.reviewStatus === 'validated') this.loadDetail(request.id, request.groupId);

        // The LIST row's own status and reviewStatus changed too, and the socket event that says
        // so is not guaranteed to arrive before the user looks. `refresh` is idempotent.
        this.patientDocumentRequestService.refresh();
      });
  }


  /* ── What the validation left behind, for the expanded row ───────────────────────────── */

  validationFilesFor(request: PatientDocumentRequestDto): PatientDocumentReturnedFileDto[] {
    return activeValidationFiles(this.details[request.id]);
  }

  /**
   * The clinician's attachments as gallery tiles.
   *
   * Its OWN cache key, separate from `galleryFor`'s. The cache exists because a binding that
   * returns a fresh array re-runs `FileGalleryComponent.ngOnChanges` on every change-detection
   * tick; sharing one key between two galleries would hand each the other's tiles.
   */
  validationGalleryFor(request: PatientDocumentRequestDto): FileObject[] {
    const files = this.validationFilesFor(request);
    const key = `validation|${request.id}|${files.map((f) => `${f.id}:${f.url || ''}`).join(',')}`;
    if (!(key in this.galleryCache)) {
      this.galleryCache[key] = mapMediaToFileObject(
        files
          .filter((file) => !!file.url)
          .map((file) => ({
            id: file.id,
            mediaUrl: file.url,
            mediaType: file.mediaType,
            mediaFileName: file.mediaFileName,
            displayIndex: file.displayIndex,
            patientId: request.patientId,
          })),
      ) || [];
    }
    return this.galleryCache[key];
  }

  /** The observations, off the detail copy — the list endpoint does not serve them. */
  validationNoteFor(request: PatientDocumentRequestDto): string | null {
    const detail = this.details[request.id];
    return detail && detail.validationNote ? detail.validationNote : null;
  }

  validatedByFor(request: PatientDocumentRequestDto): string | null {
    const detail = this.details[request.id] || request;
    return detail && detail.validatedByName ? detail.validatedByName : null;
  }

  validatedAtFor(request: PatientDocumentRequestDto): string | null {
    const detail = this.details[request.id] || request;
    return detail && detail.validatedAt ? detail.validatedAt : null;
  }

  /* ── Resend — an INVITE resend (§21.1) ────────────────────────────────────────────────── */

  /**
   * The invite this document was delivered on, or `null` for a row raised before the invite rail.
   *
   * NULL is the honest answer for the legacy case and it is load-bearing: every resend affordance
   * below is switched off by it, because there is nothing left in the system that can reach that
   * patient with that document.
   */
  inviteIdFor(request: PatientDocumentRequestDto): string | null {
    const id = request.patientInviteId;
    return id ? String(id) : null;
  }

  /**
   * A REJECTED request is resendable, and that is the point of rejecting one.
   *
   * `completed` otherwise is not — the document is in the chart. `revoked` is not. `expired` IS:
   * `resendInvite` refreshes the invite expiry, which is how a practice revives an ask the patient
   * never got round to opening.
   *
   * AND it needs an invite to resend. See `inviteIdFor`.
   */
  canResend(request: PatientDocumentRequestDto): boolean {
    if (this.stateMode) return false;
    if (!this.inviteIdFor(request)) return false;
    /*
     * THE INVITE HAS TO BE ALIVE, and until now nothing here checked.
     *
     * Returning a document COMPLETES the invite that carried it
     * (`patientInviteController.js:561`), so a row still pinned to a submitted invite has a dead
     * link — and `resendInvite` refuses it 409 rather than spending a message credit on a link
     * the patient would be turned away from. This panel drew the button anyway and every click
     * failed. `tasks/document-rejection-resend.md` §7.
     *
     * It bites on more than the rejected row that made it visible: ONE invite can carry several
     * documents, so a patient who submits the wizard having returned two of three leaves the
     * third `sent` with a completed invite behind it.
     *
     * `!== false`, never `=== true` — absent means permitted, so a server that does not send the
     * flag does not disable every resend in the product.
     */
    if (request.inviteResendable === false) return false;
    if (request.status === 'revoked') return false;
    if (request.status === 'completed') return request.reviewStatus === 'rejected';
    // `delivered` IS resendable, and deliberately: §14.2 says an information sheet never blocks a
    // duplicate and may be sent to the same patient as often as the practice likes.
    return true;
  }

  /**
   * Why a row offers no resend button. Empty when it does.
   *
   * A control that is simply absent teaches a receptionist nothing; this row is the one they came
   * to the panel to act on, so the reason has to be on it.
   */
  resendUnavailableReason(request: PatientDocumentRequestDto): string {
    /*
     * A DEAD LINK GETS ITS OWN SENTENCE, and it is checked FIRST because the row DOES have an
     * invite id — the sentence below is about rows that have none, and would be false here.
     *
     * It is the reason a receptionist is most likely to meet: the patient has submitted, so the
     * link is spent. What they need is a fresh ask, not an explanation about rails.
     */
    if (this.inviteIdFor(request) && request.inviteResendable === false) {
      return 'The link this was sent on has already been used, so it cannot be resent. '
        + 'Use Send documents to ask again — or file the document yourself if you already have it.';
    }
    if (this.inviteIdFor(request)) return '';
    if (request.status === 'revoked' || request.status === 'completed') return '';
    // A SKIPPED row also has no invite, and this sentence would be false of it: nothing was ever
    // sent, so there is no old link and no rail to blame. Its own strip says what happened and
    // what to do about it, and Send-now is the control that acts on it (§25.4).
    if (this.isSkipped(request)) return '';
    return 'This was sent before documents moved onto the patient invite, so it cannot be resent. '
      + 'Use Send documents to ask again.';
  }

  /**
   * The cooldown, per INVITE rather than per document row (§21.1).
   *
   * `nextAllowedResendAt` on the row is the standalone rail's, computed from a `resendCount` that
   * nothing writes any more. What governs a resend now is the invite's own cooldown, which this
   * client learns from the 429 `resendInvite` answers with — so it is held here, against the
   * invite id, and two documents on one invite share it because one message carried both.
   */
  private inviteResendCooldown: { [inviteId: string]: string } = {};

  /**
   * The cooldown in force for this row: what a 429 told us, else whatever the server put on the
   * row.
   *
   * The 429 wins because it is the newer fact — the row was serialised before this resend was
   * attempted. The row is still consulted rather than ignored: if the server populates
   * `nextAllowedResendAt` from the INVITE (which is the cooldown that matters — §21.1), the button
   * is correctly disabled the moment the panel loads, without waiting for a refusal the practice
   * has to earn by clicking.
   */
  private inviteCooldownAt(request: PatientDocumentRequestDto): string | null {
    const inviteId = this.inviteIdFor(request);
    const learned = inviteId ? this.inviteResendCooldown[inviteId] : null;
    return learned || request.nextAllowedResendAt || null;
  }

  /** False while the invite cooldown is still running. */
  resendAllowed(request: PatientDocumentRequestDto): boolean {
    return getResendCooldown(this.inviteCooldownAt(request)).canResend;
  }

  resendTooltip(request: PatientDocumentRequestDto): string {
    const cooldown = getResendCooldown(this.inviteCooldownAt(request));
    if (!cooldown.canResend) {
      return `Resend available in ${formatCooldownRemaining(cooldown.secondsRemaining)}`;
    }
    return request.reviewStatus === 'rejected'
      ? 'Send the invite again to ask the patient for this document'
      : 'Send the patient their invite again';
  }

  /**
   * Send the invite again. THE SAME LINK — nothing rotates (§21.1).
   *
   * `resendInvite` re-sends `invite.shortToken || invite.inviteToken`, refuses an invite that is
   * no longer active rather than spending a message credit on a link the patient would be turned
   * away from, and holds the cooldown. All three are the server's, and this method does not
   * second-guess any of them: a refusal is shown in the server's own words.
   */
  async resend(request: PatientDocumentRequestDto, event?: MouseEvent): Promise<void> {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.canResend(request) || this.busyRequestId) return;

    const inviteId = this.inviteIdFor(request);
    if (!inviteId) return;

    if (!this.resendAllowed(request)) {
      this.showToastMessage(this.resendTooltip(request));
      return;
    }

    // NO WARNING ABOUT A DEAD LINK, because nothing dies. The old confirmation said "a new link is
    // created and the link that was sent before will stop working immediately"; that was true of
    // the per-document token and is false of an invite resend, and a warning that is false is
    // worse than no warning.
    const confirmed = await this.showConfirmationDialog(
      'RESEND INVITE',
      `Send this patient their invite again? It carries "${this.documentName(request)}" and `
      + `anything else they still have outstanding. The link they already have keeps working — `
      + `this simply sends it again.`,
      undefined,
      'Cancel',
      'Resend',
    );
    if (!confirmed) return;

    this.busyRequestId = request.id;

    this.patientInviteService.resendInvite(inviteId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.busyRequestId = null;
          this.showToastMessage(
            `The invite carrying "${this.documentName(request)}" was sent again.`);
        },
        error: (error: any) => {
          this.busyRequestId = null;
          // A 429 carries the moment the button becomes usable. Written against the INVITE, so
          // every document delivered on it disables together — one message carried them all.
          const nextAllowed = error?.nextAllowedResendAt || error?.error?.nextAllowedResendAt;
          if (nextAllowed) this.inviteResendCooldown[inviteId] = String(nextAllowed);
          this.showToastMessage(this.describeInviteResendFailure(error));
        },
      });
  }

  /**
   * A resend refusal, in the SERVER'S words wherever it sent any.
   *
   * `resendInvite` answers 409 with `status: 'completed' | 'revoked'` and a sentence that already
   * tells the practice what to do instead ("Create a new invite if further information is
   * needed"), so paraphrasing it here would only lose the instruction.
   */
  private describeInviteResendFailure(error: any): string {
    const served = error?.error?.message || error?.message;
    if (typeof served === 'string' && served !== '') return served;
    return 'The invite could not be sent again. Please check your connection and try again.';
  }

  /*
   * `defaultChannels()` USED TO LIVE HERE and is gone with the per-document resend.
   *
   * It chose email-or-SMS for `POST /patient-document-requests/:id/resend`. An invite resend takes
   * no channels: `resendInvite` decides from the invite and the patient it belongs to, which is
   * one fewer place for this client to hold an opinion the server also holds.
   */

  /* ── Revoke ───────────────────────────────────────────────────────────────────────────── */

  /**
   * A returned document cannot be cancelled — what came back is already in the record.
   *
   * Neither can a `delivered` one (§14.4): there is no link to stop working, the file is already
   * in the patient's inbox, and a Cancel button that could not take it back would be a promise
   * this practice cannot keep.
   */
  canRevoke(request: PatientDocumentRequestDto): boolean {
    // Nor a SKIPPED one (§23.1). It is already terminal and nothing was ever sent, so the
    // confirmation's promise — "the patient's link stops working straight away" — describes a
    // link that does not exist. Cancelling it would also lose the reason the practice needs.
    if (this.isSkipped(request)) return false;
    return request.status !== 'completed' && request.status !== 'revoked'
      && request.status !== 'delivered';
  }

  async revoke(request: PatientDocumentRequestDto, event?: MouseEvent): Promise<void> {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.canRevoke(request) || this.busyRequestId) return;

    const confirmed = await this.showConfirmationDialog(
      'CANCEL DOCUMENT REQUEST',
      `Cancel "${this.documentName(request)}"? The patient's link stops working straight away.`,
      undefined,
      'Keep it',
      'Cancel request',
      false,
    );
    if (!confirmed) return;

    this.busyRequestId = request.id;

    this.patientDocumentRequestService.revokeRequest(request.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.busyRequestId = null;
          this.showToastMessage('That document request was cancelled.');
        },
        error: (error: any) => {
          this.busyRequestId = null;
          const failure = describeDocumentFailure(error, this.documentName(request));
          this.showToastMessage(failure.message);
          // ALREADY_COMPLETED means our copy of the row is stale — the document came back while
          // this screen was open. Re-read rather than leaving a Cancel button on a returned one.
          if (failure.code === 'ALREADY_COMPLETED') this.patientDocumentRequestService.refresh();
        },
      });
  }

  /* ── Send ─────────────────────────────────────────────────────────────────────────────── */

  openSendDialog(event?: MouseEvent): void {
    event?.preventDefault();
    event?.stopPropagation();

    const patient = this.patientService.patient;
    if (!patient) {
      this.showToastMessage('Open a patient before sending a document.');
      return;
    }

    // No `afterClosed` handling: a dedupe refusal announces itself through
    // `PatientDocumentRequestService.focusRequest$`, which this panel is already subscribed to, so
    // the sidebar action gets the same behaviour without the patient file knowing about it.
    this.dialog.open(PatientDocumentSendDialogComponent, {
      width: '520px',
      maxWidth: '95vw',
      data: { patient },
    });
  }

  /** Open the panel and the named row. Used by the dedupe "show that request" affordance. */
  private focusRequest(id: string): void {
    if (!id) return;
    // `open()` is a no-op on an already-open panel and emits `(opened)` otherwise, which routes
    // through `toggleExpanded` and tells the patient file to remember the panel is open.
    this.panel?.open();
    const request = (this.requests || []).filter((r) => r.id === id)[0];
    this.expandedRequestId = id;
    if (request && this.canOpen(request)) this.loadDetail(id, request.groupId);
  }

  trackByRequest(_index: number, request: PatientDocumentRequestDto): string {
    return request.id;
  }

  trackByMedia(index: number, media: PatientDocumentReturnedFileDto): string {
    return media.id || String(index);
  }
}
